// Single source of truth for the 6 matching dimensions surfaced in the
// COD screen. The cod screen, the WeightAdjustModal, and any future XAI
// surface all import from here so a future dimension change (rename, add,
// reorder) ripples without drift.
//
// Keys mirror the backend constant MatchesController::RECALC_DIMENSIONS —
// keeping them aligned means a recalculate request roundtrip is just
// `weights[key]` on both sides with no translation layer.

export type MatchDimensionKey =
  | "distance"
  | "qualification"
  | "availability"
  | "load"
  | "history"
  | "rating";

export interface MatchDimension {
  key:        MatchDimensionKey;
  label:      string;
  scoreKey:   string;                     // Match payload field carrying the sub-score
  metaFor:    (m: any) => string;         // Sub-label rendered under the bar
  description?: string;                   // Optional tooltip / muted helper text
}

export const MATCH_DIMENSIONS: MatchDimension[] = [
  {
    key: "distance",
    label: "거리",
    scoreKey: "score_distance",
    // 좌표 누락 시 distance_km은 null — 가짜 km 대신 "거리 미상" 표기(점수는 중립 60).
    metaFor: (m) => (m.distance_km == null ? "거리 미상" : `${m.distance_km}km`),
    description: "어르신 ↔ 매니저 직선 거리",
  },
  {
    key: "qualification",
    label: "자격",
    scoreKey: "score_qualification",
    metaFor: (m) => m.care_manager.qualification,
    description: "보유 자격증 적합도",
  },
  {
    key: "availability",
    label: "가용",
    scoreKey: "score_availability",
    // 점수(score_availability)와 동일 출처인 '이번 주 남은 근무시간 여유'를 라벨로 표시.
    // weekly_hours 필드는 서버가 upcoming(아직 안 끝난) 방문시간으로 내려준다 —
    // 이미 끝난 방문은 가용에서 빠지므로 잔여시간도 그 기준이어야 점수와 맞는다.
    metaFor: (m) => {
      const max  = m.care_manager.max_weekly_hours ?? 40;
      const used = m.care_manager.weekly_hours ?? 0;
      return `잔여 ${Math.max(max - used, 0)}h`;
    },
    description: "주간 근무시간 여유",
  },
  {
    key: "load",
    label: "부하",
    scoreKey: "score_load",
    metaFor: (m) => `부하 ${Math.round(m.care_manager.load_ratio * 100)}%`,
    description: "현재 배정된 케이스 부하",
  },
  {
    key: "history",
    label: "과거관계",
    scoreKey: "score_history",
    // 이 어르신과의 실제 과거 방문·케이스 횟수를 라벨로 — 점수와 같은 출처.
    metaFor: (m) => {
      const v = m.history_visits ?? 0;
      const c = m.history_cases ?? 0;
      if (!v && !c) return "관계 이력 없음";
      const parts: string[] = [];
      if (v) parts.push(`방문 ${v}회`);
      if (c) parts.push(`케이스 ${c}건`);
      return parts.join(" · ");
    },
    description: "이 어르신과의 과거 방문·케이스 관계",
  },
  {
    key: "rating",
    label: "평가",
    scoreKey: "score_rating",
    metaFor: (m) => `${m.care_manager.rating}/5`,
    description: "보호자/케이스 평가 평균",
  },
];

// Default weights for the recalculation modal — uniform-ish with a slight
// distance bias, matching the dashboard defaults. Tuned to total 100.
export const DEFAULT_WEIGHTS: Record<MatchDimensionKey, number> = {
  distance:      25,
  qualification: 20,
  availability:  15,
  load:          15,
  history:       15,
  rating:        10,
};
